import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    FiMessageSquare, FiSend, FiX, FiMinimize2,
    FiAlertCircle, FiCheck, FiCheckCircle, FiFlag, FiShield
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { usePresence } from '../../hooks/usePresence';
import UserActionMenu from '../user/UserActionMenu';
import ReportMessageModal from './ReportMessageModal';
import LinkWarningModal from './LinkWarningModal';
import logger from '../../utils/logger';
import './DirectMessaging.css';

/**
 * DirectMessaging - Real-time DM system for PRD-020 Module 2
 * Live direct messaging between users with Realtime subscriptions
 */
export default function DirectMessaging({ recipientId, recipientName, onClose }) {
    const { supabase, currentUser } = useAuth();
    const { isUserOnline } = usePresence('crewlink');

    const [conversationId, setConversationId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [messageText, setMessageText] = useState('');
    const [sending, setSending] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [reportingMessage, setReportingMessage] = useState(null);
    const [linkWarning, setLinkWarning] = useState(null); // { url, senderName }
    const [isRecipientTrusted, setIsRecipientTrusted] = useState(false);

    const messagesEndRef = useRef(null);
    const messageChannelRef = useRef(null);

    // Scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Check if recipient is trusted for links
    useEffect(() => {
        if (!recipientId) return;

        const checkTrustStatus = async () => {
            try {
                const { data, error } = await supabase.rpc('check_link_trust', {
                    p_trusted_user_id: recipientId
                });

                if (error) {
                    logger.error('DirectMessaging', 'Error checking trust status:', error);
                    setIsRecipientTrusted(false);
                } else {
                    setIsRecipientTrusted(data === true);
                }
            } catch (err) {
                logger.error('DirectMessaging', 'Error checking trust:', err);
                setIsRecipientTrusted(false);
            }
        };

        checkTrustStatus();
    }, [recipientId, supabase]);

    // Initialize conversation
    useEffect(() => {
        if (!recipientId) return;

        const initConversation = async () => {
            setLoading(true);
            setError(null);

            try {
                // Get or create conversation
                const { data, error: convError } = await supabase.rpc(
                    'get_or_create_conversation',
                    { p_other_user_id: recipientId }
                );

                if (convError) throw convError;

                if (!data.success) {
                    // Check if error is due to DMs being disabled
                    if (data.code === 'DM_DISABLED') {
                        throw new Error('This user has disabled direct messages and cannot receive your messages.');
                    }
                    throw new Error(data.message || 'Failed to create conversation');
                }

                setConversationId(data.conversation_id);

                // Load messages
                await loadMessages(data.conversation_id);

                // Mark messages as read
                await supabase.rpc('mark_messages_as_read', {
                    p_conversation_id: data.conversation_id
                });

            } catch (err) {
                logger.error('DirectMessaging', 'Error initializing conversation:', err);
                setError(err.message || 'Failed to load conversation');
            } finally {
                setLoading(false);
            }
        };

        initConversation();
    }, [recipientId, supabase]);

    // Load messages
    const loadMessages = async (convId) => {
        try {
            // Use RPC function to get messages with profile info
            const { data, error: messagesError } = await supabase
                .rpc('get_conversation_messages', { p_conversation_id: convId });

            if (messagesError) throw messagesError;

            // Transform data to match expected format
            const transformedMessages = (data || []).map(msg => ({
                ...msg,
                sender: {
                    id: msg.sender_id,
                    username: msg.sender_username,
                    display_name: msg.sender_display_name
                },
                recipient: {
                    id: msg.recipient_id,
                    username: msg.recipient_username,
                    display_name: msg.recipient_display_name
                }
            }));

            setMessages(transformedMessages);
            setTimeout(scrollToBottom, 100);
        } catch (err) {
            logger.error('DirectMessaging', 'Error loading messages:', err);
        }
    };

    // Subscribe to new messages
    useEffect(() => {
        if (!conversationId) return;

        logger.debug('DirectMessaging', 'Setting up realtime subscription for conversation:', conversationId);

        const channel = supabase
            .channel(`dm:${conversationId}`)
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'dm_messages',
                    filter: `conversation_id=eq.${conversationId}`
                },
                async (payload) => {
                    // CRITICAL FIX: Made callback async instead of using IIFE
                    logger.debug('DirectMessaging', 'New message received:', payload);
                    const newMessage = payload.new;

                    try {
                        // Batch both profile fetches for better performance
                        const [senderResult, recipientResult] = await Promise.all([
                            supabase
                                .from('profiles')
                                .select('id, username, display_name')
                                .eq('id', newMessage.sender_id)
                                .single(),
                            supabase
                                .from('profiles')
                                .select('id, username, display_name')
                                .eq('id', newMessage.recipient_id)
                                .single()
                        ]);

                        const messageWithProfiles = {
                            ...newMessage,
                            sender: senderResult.data || {
                                id: newMessage.sender_id,
                                username: 'Unknown',
                                display_name: null
                            },
                            recipient: recipientResult.data || {
                                id: newMessage.recipient_id,
                                username: 'Unknown',
                                display_name: null
                            }
                        };

                        logger.debug('DirectMessaging', 'Adding message to state:', messageWithProfiles);

                        // Prevent duplicate messages
                        setMessages(prev => {
                            if (prev.some(msg => msg.id === messageWithProfiles.id)) {
                                logger.debug('DirectMessaging', 'Duplicate message detected, skipping');
                                return prev;
                            }
                            return [...prev, messageWithProfiles];
                        });

                        setTimeout(scrollToBottom, 100);

                        // Mark as read if from other user
                        if (newMessage.sender_id !== currentUser?.id) {
                            await supabase.rpc('mark_messages_as_read', {
                                p_conversation_id: conversationId
                            });
                        }
                    } catch (err) {
                        logger.error('DirectMessaging', 'Error handling new message:', err);
                        // Don't throw - just log and continue
                    }
                }
            )
            .subscribe((status) => {
                logger.debug('DirectMessaging', 'Subscription status:', status);
            });

        messageChannelRef.current = channel;

        return () => {
            logger.debug('DirectMessaging', 'Cleaning up subscription');
            if (messageChannelRef.current) {
                messageChannelRef.current.unsubscribe();
            }
        };
    }, [conversationId, supabase, currentUser]);

    // Send message
    const handleSendMessage = async (e) => {
        e.preventDefault();

        if (!messageText.trim() || sending) return;

        setSending(true);
        setError(null);

        try {
            const { data, error: sendError } = await supabase.rpc('send_dm_message', {
                p_recipient_id: recipientId,
                p_content: messageText.trim()
            });

            if (sendError) throw sendError;

            if (!data.success) {
                // Check if error is due to DMs being disabled
                if (data.code === 'DM_DISABLED') {
                    throw new Error('This user has disabled direct messages and cannot receive your messages.');
                }
                throw new Error(data.message || 'Failed to send message');
            }

            setMessageText('');
        } catch (err) {
            logger.error('DirectMessaging', 'Error sending message:', err);
            setError(err.message || 'Failed to send message');
        } finally {
            setSending(false);
        }
    };

    // Add user to link trust list
    const handleTrustUser = async () => {
        try {
            const { data, error } = await supabase.rpc('add_link_trust', {
                p_trusted_user_id: recipientId
            });

            if (error) throw error;

            if (data.success) {
                setIsRecipientTrusted(true);
                logger.debug('DirectMessaging', 'User added to link trust list');
            } else {
                logger.error('DirectMessaging', 'Failed to add trust:', data.message);
            }
        } catch (err) {
            logger.error('DirectMessaging', 'Error adding link trust:', err);
        }
    };

    // Format timestamp (MEMOIZED for performance)
    const formatTime = useCallback((timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(diffDays > 365 ? { year: 'numeric' } : {})
        });
    }, []); // No dependencies - pure function

    // HIGH-001 FIX: Safer URL detection with length limits to prevent ReDoS
    const urlRegex = useMemo(() => /(https?:\/\/[^\s]{1,2048})/g, []);
    const MAX_MESSAGE_LENGTH_FOR_LINK_DETECTION = 10000;

    // Detect if message contains links (MEMOIZED for performance)
    const detectLinks = useCallback((text) => {
        // Prevent ReDoS with excessive message lengths
        if (!text || text.length > MAX_MESSAGE_LENGTH_FOR_LINK_DETECTION) {
            logger.warn('DirectMessaging', 'Message too long for link detection or empty');
            return false;
        }

        // Use safer regex with length limit
        return urlRegex.test(text);
    }, [urlRegex]);

    // Validate and sanitize URLs (MEMOIZED for performance)
    const isValidUrl = useCallback((urlString) => {
        try {
            const url = new URL(urlString);
            // Only allow http and https protocols
            if (!['http:', 'https:'].includes(url.protocol)) {
                return false;
            }
            // Warn about internationalized domain names (potential phishing)
            if (/[\u0080-\uFFFF]/.test(url.hostname)) {
                logger.warn('DirectMessaging', 'URL contains non-ASCII characters:', urlString);
            }
            return true;
        } catch {
            return false;
        }
    }, []);

    // Handle link click - show warning modal for trusted users (MEMOIZED)
    const handleLinkClick = useCallback((e, url, senderName) => {
        e.preventDefault();
        setLinkWarning({ url, senderName });
    }, []);

    // Render message content with link handling (MEMOIZED for performance)
    const renderMessageContent = useCallback((message) => {
        const content = message.content;
        const senderName = message.sender_id === currentUser?.id
            ? 'You'
            : (message.sender?.display_name || message.sender?.username || recipientName);

        const hasLinks = detectLinks(content);
        const isOwnMessage = message.sender_id === currentUser?.id;

        // If no links, return plain text
        if (!hasLinks) {
            return <span>{content}</span>;
        }

        // Own messages: always show links (you trust yourself)
        if (isOwnMessage) {
            const parts = content.split(urlRegex);
            const matches = content.match(urlRegex) || [];
            let matchIndex = 0;

            return (
                <span>
                    {parts.map((part, index) => {
                        if (index % 2 === 1) {
                            const url = matches[matchIndex++];
                            // Validate URL before rendering as link
                            if (isValidUrl(url)) {
                                return (
                                    <a
                                        key={index}
                                        href={url}
                                        className="message-link"
                                        onClick={(e) => handleLinkClick(e, url, senderName)}
                                        rel="noopener noreferrer"
                                    >
                                        {url}
                                    </a>
                                );
                            } else {
                                // Invalid URL - render as plain text
                                return <span key={index} className="invalid-url">{url}</span>;
                            }
                        }
                        return part;
                    })}
                </span>
            );
        }

        // Other user's messages: check if sender is trusted
        if (!isRecipientTrusted) {
            return (
                <div className="message-link-blocked">
                    <FiShield className="blocked-icon" />
                    <div className="blocked-content">
                        <span className="blocked-text">
                            Link blocked for your safety.
                        </span>
                        <button
                            className="btn-enable-links"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleTrustUser();
                            }}
                            title="Allow links from this user"
                        >
                            Enable links from {senderName}
                        </button>
                    </div>
                </div>
            );
        }

        // Trusted user - render with clickable links and warnings
        const parts = content.split(urlRegex);
        const matches = content.match(urlRegex) || [];
        let matchIndex = 0;

        return (
            <span>
                {parts.map((part, index) => {
                    if (index % 2 === 1) {
                        // This is a URL
                        const url = matches[matchIndex++];
                        // Validate URL before rendering as link
                        if (isValidUrl(url)) {
                            return (
                                <a
                                    key={index}
                                    href={url}
                                    className="message-link"
                                    onClick={(e) => handleLinkClick(e, url, senderName)}
                                    rel="noopener noreferrer"
                                >
                                    {url}
                                </a>
                            );
                        } else {
                            // Invalid URL - render as plain text
                            return <span key={index} className="invalid-url">{url}</span>;
                        }
                    }
                    return part;
                })}
            </span>
        );
    }, [currentUser?.id, recipientName, detectLinks, urlRegex, isValidUrl, isRecipientTrusted, handleLinkClick, handleTrustUser]);

    if (minimized) {
        return (
            <div className="dm-window minimized">
                <div className="dm-header" onClick={() => setMinimized(false)}>
                    <div className="dm-header-info">
                        <FiMessageSquare className="dm-icon" />
                        <span>{recipientName}</span>
                        {isUserOnline(recipientId) && <span className="online-dot">●</span>}
                    </div>
                    <div className="dm-header-actions">
                        <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="btn-header-action">
                            <FiX />
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dm-window">
            <div className="dm-header">
                <div className="dm-header-info">
                    <FiMessageSquare className="dm-icon" />
                    <div>
                        <div className="dm-recipient-name">
                            {recipientName}
                            {isRecipientTrusted && (
                                <span className="trust-badge" title="Links enabled from this user">
                                    <FiShield />
                                </span>
                            )}
                        </div>
                        {isUserOnline(recipientId) && (
                            <div className="dm-status-online">● Online</div>
                        )}
                    </div>
                </div>
                <div className="dm-header-actions">
                    <UserActionMenu
                        userId={recipientId}
                        userName={recipientName}
                        onActionComplete={(action) => {
                            if (action === 'block') {
                                onClose(); // Close DM window when user is blocked
                            } else if (action === 'trust') {
                                setIsRecipientTrusted(true); // Update trust state when user is trusted
                            } else if (action === 'untrust') {
                                setIsRecipientTrusted(false); // Update trust state when user is untrusted
                            }
                        }}
                    />
                    <button onClick={() => setMinimized(true)} className="btn-header-action">
                        <FiMinimize2 />
                    </button>
                    <button onClick={onClose} className="btn-header-action">
                        <FiX />
                    </button>
                </div>
            </div>

            {error && (
                <div className="dm-error">
                    <FiAlertCircle />
                    <span>{error}</span>
                </div>
            )}

            <div className="dm-messages">
                {loading ? (
                    <div className="dm-loading">
                        <div className="spinner"></div>
                        <span>Loading messages...</span>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="dm-empty">
                        <FiMessageSquare className="empty-icon" />
                        <p>No messages yet</p>
                        <small>Send a message to start the conversation</small>
                    </div>
                ) : (
                    <>
                        {messages.map((message) => {
                            const isOwn = message.sender_id === currentUser.id;
                            return (
                                <div
                                    key={message.id}
                                    className={`dm-message ${isOwn ? 'own' : 'other'}`}
                                >
                                    <div className="message-content">
                                        {renderMessageContent(message)}
                                    </div>
                                    <div className="message-meta">
                                        <span className="message-time">
                                            {formatTime(message.created_at)}
                                        </span>
                                        {isOwn ? (
                                            <span className="message-status">
                                                {message.is_read ? (
                                                    <FiCheckCircle className="read-icon" title="Read" />
                                                ) : (
                                                    <FiCheck className="sent-icon" title="Sent" />
                                                )}
                                            </span>
                                        ) : (
                                            <button
                                                className="btn-report-message"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setReportingMessage(message);
                                                }}
                                                title="Report this message"
                                            >
                                                <FiFlag />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            <form className="dm-input-container" onSubmit={handleSendMessage}>
                <input
                    type="text"
                    className="dm-input"
                    placeholder="Type a message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    maxLength={2000}
                    disabled={sending || loading}
                />
                <button
                    type="submit"
                    className="btn-send"
                    disabled={!messageText.trim() || sending || loading}
                >
                    {sending ? <div className="spinner-small" /> : <FiSend />}
                </button>
            </form>

            {/* Report Message Modal */}
            {reportingMessage && (
                <ReportMessageModal
                    isOpen={!!reportingMessage}
                    message={reportingMessage}
                    onClose={() => setReportingMessage(null)}
                    onReportSubmitted={() => {
                        setReportingMessage(null);
                        // Optionally show success message
                        logger.debug('DirectMessaging', 'Message reported successfully');
                    }}
                />
            )}

            {/* Link Warning Modal */}
            {linkWarning && (
                <LinkWarningModal
                    url={linkWarning.url}
                    senderName={linkWarning.senderName}
                    onClose={() => setLinkWarning(null)}
                    onConfirm={() => setLinkWarning(null)}
                />
            )}
        </div>
    );
}

// PropTypes for type checking
DirectMessaging.propTypes = {
    /** UUID of the user to message */
    recipientId: PropTypes.string.isRequired,
    /** Display name of the recipient */
    recipientName: PropTypes.string.isRequired,
    /** Callback when DM window is closed */
    onClose: PropTypes.func.isRequired
};
